import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowRight, Warning } from "@phosphor-icons/react";
import { CategoryIcon, getIcon } from "@/lib/categoryIcons";

/**
 * Delete a category, asking where its transactions should go.
 *
 * Deleting a category that is still in use would either strand its
 * transactions under a name the picker no longer offers or silently discard
 * their categorisation, so the destination is asked for up front and the
 * confirm button stays disabled until one is chosen. Only categories of the
 * same kind are offered: an expense row cannot be filed under an income
 * category, and the API rejects it anyway.
 */
export default function DeleteCategoryDialog({ open, onOpenChange, category, categories, onDeleted }) {
  const [target, setTarget] = useState("");
  const [deleting, setDeleting] = useState(false);

  const count = category?.transaction_count || 0;

  const options = useMemo(
    () =>
      (categories || []).filter(
        (c) => c.kind === category?.kind && c.id !== category?.id,
      ),
    [categories, category],
  );

  useEffect(() => {
    if (open) setTarget("");
  }, [open, category]);

  const confirm = async () => {
    if (count && !target) return;
    setDeleting(true);
    try {
      const qs = count ? `?reassign_to=${encodeURIComponent(target)}` : "";
      const res = await api.delete(`/categories/${category.id}${qs}`);
      const { moved, moved_to: movedTo } = res.data || {};
      toast.success(
        moved
          ? `“${category.name}” deleted — ${moved} transaction${moved === 1 ? "" : "s"} moved to “${movedTo}”`
          : `“${category.name}” deleted`,
      );
      onOpenChange(false);
      onDeleted && onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete category");
    } finally {
      setDeleting(false);
    }
  };

  if (!category) return null;

  const TargetIcon = getIcon(options.find((o) => o.id === target)?.icon);
  const targetCategory = options.find((o) => o.id === target);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-md" data-testid="delete-category-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">
            Delete “{category.name}”?
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {count
              ? `${count} transaction${count === 1 ? "" : "s"} ${count === 1 ? "is" : "are"} filed under this category. Choose where ${count === 1 ? "it" : "they"} should go.`
              : "Nothing is filed under this category, so it can be removed straight away."}
          </DialogDescription>
        </DialogHeader>

        {count > 0 && (
          <div className="space-y-4">
            {/* from -> to */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 border border-border rounded-md px-3 py-2 flex-1 min-w-0">
                <CategoryIcon category={category} size={16} />
                <span className="text-sm truncate">{category.name}</span>
              </div>
              <ArrowRight size={16} className="shrink-0 text-muted-foreground" />
              <div className="flex items-center gap-2 border border-dashed border-border rounded-md px-3 py-2 flex-1 min-w-0">
                {targetCategory ? (
                  <>
                    <TargetIcon size={16} weight="bold" color={targetCategory.color} />
                    <span className="text-sm truncate">{targetCategory.name}</span>
                  </>
                ) : (
                  <span className="text-sm text-muted-foreground">Pick one…</span>
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">
                Move transactions to
              </Label>
              {options.length ? (
                <Select value={target} onValueChange={setTarget}>
                  <SelectTrigger className="mt-1 bg-background" data-testid="reassign-select">
                    <SelectValue placeholder={`Select another ${category.kind} category`} />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-64">
                    {options.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`reassign-option-${c.name}`}>
                        <span className="flex items-center gap-2">
                          <CategoryIcon category={c} size={15} />
                          {c.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="mt-2 text-sm text-expense flex items-start gap-2">
                  <Warning size={16} className="mt-0.5 shrink-0" />
                  This is your only {category.kind} category. Create another one
                  first, then delete this.
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="delete-category-cancel">
            Cancel
          </Button>
          <Button
            data-testid="delete-category-confirm"
            onClick={confirm}
            disabled={deleting || (count > 0 && !target)}
            className="bg-expense text-white hover:bg-expense/90 font-semibold"
          >
            {deleting ? "Deleting…" : count ? "Move & delete" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
